import { useState, useEffect } from 'react';

export default function MvpHero() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* Sticky top bar */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-stone-200' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">S</span>
            </div>
            <span className={`font-semibold text-sm tracking-tight transition-colors ${scrolled ? 'text-stone-800' : 'text-white'}`}>
              Submit.AI
            </span>
            <span className={`text-xs transition-colors ${scrolled ? 'text-stone-400' : 'text-white/50'}`}>/</span>
            <span className={`text-xs transition-colors ${scrolled ? 'text-stone-500' : 'text-white/70'}`}>
              Proposta Fase 1
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className={`text-xs transition-colors ${scrolled ? 'text-stone-400' : 'text-white/60'}`}>
              PROPOSTA-SUBMITAI-FASE1 · v1.0
            </span>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
              <span className="text-emerald-400 text-xs font-medium">Válida até 06/05/2026</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative min-h-screen bg-[#071210] overflow-hidden flex flex-col justify-center">
        {/* Background grid */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(16,185,129,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}></div>

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-amber-500/8 blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-28 pb-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 mb-8">
            <i className="ri-rocket-line text-emerald-400 text-xs w-4 h-4 flex items-center justify-center"></i>
            <span className="text-emerald-400 text-xs font-medium tracking-wide uppercase">Fase 1 — Plataforma Fundação</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight tracking-tight">
            Proposta Fase 1
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">
              Submit.AI
            </span>
          </h1>

          <p className="text-xl text-stone-300 mb-2 font-light">
            Do zero à submissão real de propostas FINEP em 4 meses
          </p>
          <p className="text-stone-400 mb-6 text-base max-w-2xl">
            A Fase 1 entrega o ciclo completo de captação: monitoramento automático de editais, análise estruturada, matching com projetos do instituto e geração de propostas prontas para submissão ao FINEP.
          </p>

          {/* Flow */}
          <div className="flex flex-wrap items-center gap-2 mb-12">
            {[
              { label: 'Descoberta de Edital' },
              { label: '→', arrow: true },
              { label: 'Triagem e Matching' },
              { label: '→', arrow: true },
              { label: 'Montagem da Proposta' },
              { label: '→', arrow: true },
              { label: 'Revisão e Conformidade' },
              { label: '→', arrow: true },
              { label: 'Submissão' },
            ].map((step, i) =>
              step.arrow ? (
                <span key={i} className="text-stone-600 text-lg font-light">{step.label}</span>
              ) : (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/20 text-emerald-300 text-xs font-medium">
                  {step.label}
                </div>
              )
            )}
          </div>

          {/* Meta cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
            {[
              { icon: 'ri-file-text-line', label: 'Documento', value: 'SUBMITAI-FASE1' },
              { icon: 'ri-calendar-line', label: 'Data', value: '06/04/2026' },
              { icon: 'ri-time-line', label: 'Duração', value: '4 meses' },
              { icon: 'ri-map-pin-line', label: 'Local', value: 'Manaus/AM' },
            ].map((m) => (
              <div key={m.label} className="p-4 rounded-xl border border-white/8 bg-white/4 backdrop-blur">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${m.icon} text-emerald-400 text-sm w-4 h-4 flex items-center justify-center`}></i>
                  <span className="text-stone-500 text-xs uppercase tracking-wide">{m.label}</span>
                </div>
                <p className="text-white text-sm font-semibold">{m.value}</p>
              </div>
            ))}
          </div>

          {/* Phase summary */}
          <div className="p-6 rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold">Versão 1</span>
              <span className="text-stone-400 text-xs">90 dias de desenvolvimento + 30 dias de ajustes e testes finais</span>
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">Plataforma Fundação — Ciclo Completo End-to-End</h3>
            <p className="text-stone-400 text-sm mb-5 leading-relaxed">
              Monitoramento automático de editais, análise estruturada com IA, matching inteligente com o portfólio do instituto e geração completa de propostas com checklist de conformidade e exportação para submissão.
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {['Coletor de Editais', 'Agente Analisador', 'Portfólio e Matching', 'Dashboard e Gestão', 'Base de Conhecimento', 'Agente Escritor', 'Exportação e Conformidade', 'Editor e Monitoramento'].map((mod) => (
                <span key={mod} className="px-2.5 py-1 rounded-md bg-white/8 text-stone-400 text-xs border border-white/8">{mod}</span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-emerald-500/20">
              <div>
                <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">Investimento Total — Fase 1</p>
                <p className="text-white font-bold text-4xl">R$ 357.000</p>
                <p className="text-stone-400 text-xs mt-1">Ciclo completo do zero à submissão de propostas reais</p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-stone-400 text-xs mb-1">Forma de pagamento</p>
                <p className="text-emerald-400 font-semibold text-sm">50% início (R$ 178.500) · 20% meio (R$ 71.400) · 30% entrega (R$ 107.100)</p>
                <p className="text-stone-500 text-xs mt-1">Sem compromisso com fases futuras</p>
              </div>
            </div>
          </div>

          {/* O que você consegue ao final */}
          <div className="mt-6 p-5 rounded-xl border border-white/8 bg-white/4">
            <p className="text-stone-400 text-xs uppercase tracking-wide mb-3">Com a Fase 1 você já consegue</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                'Monitorar 100% das chamadas FINEP automaticamente, sem varredura manual',
                'Receber análise estruturada de cada edital em minutos após publicação',
                'Ver quais projetos do instituto têm maior aderência a cada edital',
                'Gerar automaticamente todas as seções obrigatórias de uma proposta FINEP',
                'Exportar a proposta com checklist de conformidade antes de submeter',
                'Versionar e comparar rascunhos sem perder histórico de alterações',
                'Reduzir de 3 a 4 analistas para 1 analista de revisão',
                'Aumentar o volume de propostas submetidas sem contratar mais pessoas',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-3 h-3 mt-0.5 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                  </div>
                  <span className="text-stone-400 text-xs leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
          <span className="text-stone-400 text-sm font-medium bg-stone-900/80 px-4 py-1.5 rounded-full">Explore a proposta completa ↓</span>
        </div>
      </div>
    </>
  );
}
